import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Breadcrumbs, P, H6 } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

//TYPE AND SERVICES

// COMPANY MASTER
import {
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

// ADDRESS
import {
  countryService,
  COUNTRY_QUERY_KEY,
} from "../../../../../Services/countryService";
import { Country } from "../../../../../Types/MasterData/Country";
import {
  provinceService,
  PROVINCE_QUERY_KEY,
} from "../../../../../Services/provinceService";
import { Province } from "../../../../../Types/MasterData/Province";
import {
  cityService,
  CITY_QUERY_KEY,
} from "../../../../../Services/cityService";
import { City } from "../../../../../Types/MasterData/City";
import {
  barangayService,
  BARANGAY_QUERY_KEY,
} from "../../../../../Services/barangayService";
import { Barangay } from "../../../../../Types/MasterData/Barangay";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  company_code: string;
  company_name: string;
}

const UpdateBillingDetailsForm: React.FC<UpdateFormProps> = ({
  toggle,
  company_code,
  company_name,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    company_code: company_code,
    company_name: company_name,
    billing_address_line1: "",
    billing_address_line2: "",
    billing_country: "",
    billing_province: "",
    billing_city: "",
    billing_barangay: "",
    billing_zip_code: "",
  });

  //FETCH COMPANY DETAILS
  const { data: CompanyData, isLoading: isFetching } = useQuery({
    queryKey: [COMPANY_QUERY_KEY, company_code],
    queryFn: () => companyprofileService.getCompanyProfile(company_code),
  });

  useEffect(() => {
    if (CompanyData) {
      setFormData({
        company_code: CompanyData.data.company_code || company_code,
        company_name: CompanyData.data.company_name || company_name,
        billing_address_line1: CompanyData.data.billing_address_line1 || "",
        billing_address_line2: CompanyData.data.billing_address_line2 || "",
        billing_country: "",
        billing_province: "",
        billing_city: "",
        billing_barangay: "",
        billing_zip_code: "",
      });
    }
  }, [CompanyData]);

  const [countryBillCode, setCountryBillCode] = useState<string | null>(null);
  const [provinceBillId, setProvinceBillId] = useState<string | null>(null);
  const [cityBillId, setCityBillId] = useState<string | null>(null);

  //LIST OF VALUE
  const { data: countryData, isLoading: isCountryLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });
  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  const {
    data: provinceBillData,
    isLoading: isProvinceBillLoading,
    isError: isProvinceBillError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryBillCode],
    queryFn: () =>
      provinceService.getProvinceByCountry(countryBillCode as string),
    enabled: !!countryBillCode,
  });

  const billprovinces: Province[] =
    provinceBillData?.data?.filter((item) => item.status === 1) ?? [];

  const billprovinceOptions = !isProvinceBillError
    ? billprovinces.map((prov) => ({
        id: prov.id,
        label: prov.name,
      }))
    : [];

  const {
    data: cityBillData,
    isLoading: isCityBillLoading,
    isError: isCityBillError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceBillId],
    queryFn: () => cityService.getCityByProvince(provinceBillId as string),
    enabled: !!provinceBillId,
  });

  const Billcities: City[] =
    cityBillData?.data?.filter((item) => item.status === 1) ?? [];

  const cityBillOptions = !isCityBillError
    ? Billcities.map((city) => ({
        id: city.id,
        label: city.name,
      }))
    : [];

  const {
    data: barangayBillData,
    isLoading: isBarangayBillLoading,
    isError: isBarangayBillError,
  } = useQuery<{ data: Barangay[] }>({
    queryKey: [BARANGAY_QUERY_KEY, cityBillId],
    queryFn: () => barangayService.getBarangayByCity(cityBillId as string),
    enabled: !!cityBillId,
  });

  const Billbarangay: Barangay[] =
    barangayBillData?.data?.filter((item) => item.status === 1) ?? [];

  const barangayBillOptions = !isBarangayBillError
    ? Billbarangay.map((barangay) => ({
        id: barangay.id,
        label: barangay.name,
      }))
    : [];

  const handleBillProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_province: "",
      billing_city: "",
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setCountryBillCode(e.target.value);
    setProvinceBillId("");
    setCityBillId("");
  };

  const handleBillCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_city: "",
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setProvinceBillId(e.target.value);
    setCityBillId("");
  };

  const handleBarangayBillSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setCityBillId(e.target.value);
  };

  const updateCompanyMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      companyprofileService.updateCompanyProfile(company_code, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      toast.success(result.message);
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error("Failed to update client profile.");
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateCompanyMutation.mutate(formData);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <H6 className="fw-bold">Company Billing Address</H6>

          <Col md="12">
            <Label>
              Address Line 1<b className="text-danger">*</b>
            </Label>
            <Input
              id="billing_address_line1"
              name="billing_address_line1"
              type="text"
              placeholder="Street / House / Building"
              maxLength={150}
              minLength={5}
              required
              value={formData.billing_address_line1}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>Address Line 2</Label>
            <Input
              id="billing_address_line2"
              name="billing_address_line2"
              type="text"
              placeholder="Apartment / Unit / Subdivision"
              maxLength={150}
              minLength={5}
              value={formData.billing_address_line2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>
              Country<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2 select-scroll"
              value={formData.billing_country}
              id="billing_country"
              name="billing_country"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleBillProvinceSelect(e);
              }}
              required
            >
              <option value="" disabled>
                {isCountryLoading ? "Loading..." : "Select Country"}
              </option>
              {!isCountryLoading &&
                countries.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              Province<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.billing_province}
              id="billing_province"
              name="billing_province"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleBillCitySelect(e);
              }}
              required
              disabled={
                isProvinceBillLoading || !countryBillCode || isProvinceBillError
              }
            >
              <option value="" selected>
                {isProvinceBillLoading
                  ? "Loading..."
                  : isProvinceBillError
                    ? "No Province found"
                    : "Select Province"}
              </option>

              {!isProvinceBillLoading &&
                billprovinceOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              City/Municipality<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.billing_city}
              id="billing_city"
              name="billing_city"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleBarangayBillSelect(e);
              }}
              required
              disabled={isCityBillLoading || !provinceBillId || isCityBillError}
            >
              <option value="" disabled selected>
                {isCityBillLoading
                  ? "Loading..."
                  : isCityBillError
                    ? "No City Found"
                    : "Select City"}
              </option>

              {!isCityBillLoading &&
                cityBillOptions.map((opt) => (
                  <option key={opt.id || "empty"} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              Barangay<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.billing_barangay}
              id="billing_barangay"
              name="billing_barangay"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
              }}
              required
              disabled={
                isBarangayBillLoading || !cityBillId || isBarangayBillError
              }
            >
              <option value="" disabled selected>
                {isBarangayBillLoading
                  ? "Loading..."
                  : isBarangayBillError
                    ? "No Barangay Found"
                    : "Select Barangay"}
              </option>

              {!isBarangayBillLoading &&
                barangayBillOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="6">
            <Label>
              ZIP Code<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              id="billing_zip_code"
              name="billing_zip_code"
              type="text"
              placeholder="XXXX"
              maxLength={4}
              minLength={4}
              value={formData.billing_zip_code}
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateCompanyMutation.isPending}
            >
              {updateCompanyMutation.isPending
                ? "Updating Client Profile..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateBillingDetailsForm;
